import { AxiosRequestConfig } from "axios";
import * as csv from "csvtojson";
import { FC, useEffect, useMemo, useState } from "react";
import LoadingOverlay from 'react-loading-overlay-ts';
import { useRecoilValue } from "recoil";
import { getReadPresignedUrl, getRequiredHeaders, getUploadPresignedUrl, importInventoryData } from "../../../api/inventoryGroupImport";
import { uploadFileToS3 } from "../../../api/inventoryImport";
import { ErrorResponse } from "../../../models/error";
import { accountDataState } from "../../../recoil/account";

const defaultRequiredHeaders = ["MedigoID", "ĐVT", "GIÁ BÁN", "Tồn Kho"]

const ImportInventoryGroupUpload: FC = () => {
    const accountInfo = useRecoilValue(accountDataState)
    const adminId = useMemo(() => {
        if (accountInfo.loginSession.userLocalType === 'ADMIN')
            return accountInfo.doctor.mId
    }, [accountInfo])

    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [requiredHeaders, setRequiredHeaders] = useState<string[]>(defaultRequiredHeaders)
    const [errors, setErrors] = useState<{
        message: string
    }[]>([])

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [loadingPercent, setLoadingPercent] = useState<number>(0)
    const [isSuccess, setIsSuccess] = useState<boolean>(false)

    const [csvInfo, setCsvInfo] = useState<{
        headers: string[],
        rows: any[],
        errors: string[]
    }>({
        headers: [],
        rows: [],
        errors: []
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (!adminId) {
                    return;
                }

                const result = await getRequiredHeaders(parseInt(adminId));
                setRequiredHeaders(result.data);
            } catch (err) {
                console.error("cannot fetch required headers: ", err);
            }
        };

        fetchData();
    }, [adminId])

    const onFileChange = (files: FileList | null) => {
        if (!files
            || files.length === 0) {
            return;
        }
        let file = files[0];

        setSelectedFile(file);
        setIsSuccess(false);

        const reader = new FileReader();
        reader.onload = (e) => {
            if (e.target) {
                const text = e.target.result;
                if (text && typeof text === 'string') {
                    processCSV(text)
                }
            }
        }
        reader.readAsText(file);
    }

    const processCSV = async (str: string) => {
        const rows = await csv.default().fromString(str);
        let headers: string[] = [];

        if (rows
            && rows.length > 0) {
            for (let key in rows[0]) {
                headers.push(key)
            }
        }
        let errors = [];

        if (!isValidHeader(headers)) {
            errors.push(`Cần có đủ các cột: ${requiredHeaders.join(', ')}`);
        }
        if (!rows
            || rows.length === 0
        ) {
            errors.push(`Không tìm thấy dữ liệu, vui lòng kiểm tra lại`)
        }

        setCsvInfo({
            headers,
            rows,
            errors
        })
    }

    const FileErrors = () => {
        let maps = csvInfo.errors.map((error, index) => {
            return <div className="alert alert-danger" key={index}>
                <img src="./images/report_problem.svg" title="Error" className="img-fluid" width="20" />
                <span className="ml-2">{error}</span>
            </div>
        })

        return <div>{maps}</div>
    }

    const ServerErrors = () => {
        let maps = errors.map((error, index) => {
            return <div className="alert alert-danger" key={index}>
                <img src="./images/report_problem.svg" title="Error" className="img-fluid" width="20" />
                <span className="ml-2">{error.message}</span>
            </div>
        })

        return <div>{maps}</div>
    }

    const isValidHeader = (headers: string[]) => {
        for (let col of requiredHeaders) {
            let temp = headers.includes(col);
            if (!temp) {
                return false;
            }
        }
        return true;
    }

    const getDisableClass = () => {
        if (isLoading
            || !selectedFile
            || csvInfo.errors.length > 0) {
            return "disabled";
        }

        return "";
    }

    const clearSelectedFile = () => {
        setSelectedFile(null);
        let element = document.getElementById("imgInp") as HTMLInputElement;
        if (element) {
            element.value = "";
        }
    }

    const onUploadClick = async () => {
        if (isLoading
            || csvInfo.errors.length > 0
            || !adminId
            || !selectedFile
        ) {
            return;
        }

        setIsLoading(true)
        setErrors([])

        try {
            let response = await getUploadPresignedUrl(parseInt(adminId), selectedFile.name);

            var options: AxiosRequestConfig = {
                headers: {
                    'Content-Type': 'text/csv'
                },
                onUploadProgress: (progressEvent) => {
                    setLoadingPercent(Math.round(progressEvent.loaded / progressEvent.total * 100));
                }
            };

            await uploadFileToS3(response.data.preSignedUrl, selectedFile, options);

            let readFilePSUReponse = await getReadPresignedUrl(parseInt(adminId), response.data.key);

            await importInventoryData(parseInt(adminId), readFilePSUReponse.data.preSignedUrl, selectedFile.name);

            setIsSuccess(true);
            setIsLoading(false);

            clearSelectedFile()
        } catch (err: any) {
            setIsLoading(false);
            if (err.response.data) {
                let errorRes: ErrorResponse = err.response.data;
                if (errorRes.error.details) {
                    setErrors(errorRes.error.details.map(errItem => {
                        if (errItem.vi) {
                            return { message: errItem.vi.message }
                        }
                        return { message: errItem.message }
                    }))
                } else {
                    setErrors([{ message: errorRes.error.message }]);
                }
            } else {
                setErrors([{ message: 'Có lỗi xảy ra, vui lòng thử lại' }]);
            }
        }
    }

    return (
        <div>
            <LoadingOverlay
                active={isLoading}
                spinner
                text={'Đang xử lý ... ' + loadingPercent + '%'}
                fadeSpeed={500}
            >
                {
                    isLoading ?
                        <div className="overlay-wrapper">
                        </div>
                        : ''
                }
            </LoadingOverlay>
            <div className="management-content">
                <div className="form-group form-file mt-5">
                    <input type="file" id="imgInp" name="img" accept=".csv" onChange={(e) => onFileChange(e.target.files)} />
                    <span className="input-label"><img src="./images/MDI-upload.svg" title="Tải lên" className="img-fluid" width="24" /> <span>Tải File/Upload kho hàng</span></span>
                </div>

                {
                    selectedFile ?
                        <div>
                            <p>Tên file: <span>{selectedFile.name}</span></p>
                            <p>Số lượng sản phẩm: {new Intl.NumberFormat().format(csvInfo.rows.length)}</p>
                            <FileErrors />
                            <ServerErrors />
                        </div>
                        : ''
                }
                {
                    isSuccess ?
                        <div className="text-center mt-5 text-success">
                            Tải dữ liệu thành công, <br />vui lòng xem chi tiết ở lịch sử tải kho.
                        </div> : ''
                }

                <div className="fixed-button">
                    <a onClick={
                        (e) => {
                            e.preventDefault();
                            onUploadClick()
                        }
                    } className={`btn btn-block ${getDisableClass()}`} href="/#">XỬ LÝ FILE</a>
                </div>
            </div>
        </div>
    )
}

export default ImportInventoryGroupUpload;
